/**
 * AWS Amplify Configuration for Advisor Panel
 *
 * Configures Cognito authentication with SSO support (Google, Microsoft).
 * Environment variables are injected by Vite at build time.
 *
 * @module shared/lib/amplify-config
 */

import { Amplify, type ResourcesConfig } from "aws-amplify";

// Environment-based configuration
// These are populated from SST outputs or .env files
const cognitoConfig = {
  userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || "",
  userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || "",
  region: import.meta.env.VITE_AWS_REGION || "eu-central-1",
  domain: import.meta.env.VITE_COGNITO_DOMAIN || "",
};

// Determine redirect URLs based on environment
const getRedirectUrls = () => {
  const baseUrl = window.location.origin;
  return {
    signIn: `${baseUrl}/auth/callback`,
    signOut: `${baseUrl}/`,
  };
};

// Amplify Auth configuration
export const amplifyConfig: ResourcesConfig = {
  Auth: {
    Cognito: {
      userPoolId: cognitoConfig.userPoolId,
      userPoolClientId: cognitoConfig.userPoolClientId,
      signUpVerificationMethod: "code",
      loginWith: {
        email: true,
        oauth: {
          domain: cognitoConfig.domain,
          scopes: ["email", "profile", "openid"],
          redirectSignIn: [getRedirectUrls().signIn],
          redirectSignOut: [getRedirectUrls().signOut],
          responseType: "code",
          providers: ["Google"] as const,
        },
      },
      userAttributes: {
        email: { required: true },
        given_name: { required: false },
        family_name: { required: false },
        phone_number: { required: false },
      },
      passwordFormat: {
        minLength: 12,
        requireLowercase: true,
        requireUppercase: true,
        requireNumbers: true,
        requireSpecialCharacters: true,
      },
    },
  },
};

/**
 * Initialize Amplify with Cognito configuration
 * Call this once at app startup (main.tsx)
 */
export function initializeAmplify(): void {
  // Only initialize if we have the required config
  if (!cognitoConfig.userPoolId || !cognitoConfig.userPoolClientId) {
    console.warn(
      "AWS Cognito configuration missing. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID environment variables."
    );
    return;
  }

  Amplify.configure(amplifyConfig);
  console.log("AWS Amplify initialized with Cognito");
}

/**
 * Check if Cognito is properly configured
 */
export function isCognitoConfigured(): boolean {
  return Boolean(cognitoConfig.userPoolId && cognitoConfig.userPoolClientId);
}

/**
 * Get Cognito configuration for debugging
 */
export function getCognitoConfig() {
  return {
    userPoolId: cognitoConfig.userPoolId,
    region: cognitoConfig.region,
    domain: cognitoConfig.domain,
    isConfigured: isCognitoConfigured(),
  };
}
